import { useScrollReveal } from '@/hooks/useScrollReveal';

const reasons = [
  {
    icon: 'ri-focus-3-line',
    title: 'Mission-Driven Work',
    desc: 'You\'re not building another food delivery app. You\'re building the digital infrastructure that determines whether a fisherman in Kerala gets a fair price and whether a family in Delhi eats safe frozen food. That\'s a mission worth waking up for.',
    stat: '18-24%',
    statLabel: 'Average price improvement for producers on our network',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Network-Effects Business',
    desc: 'Every warehouse that joins makes the network more valuable. Every transporter that signs up improves route matching. Every buyer that transacts generates data that sharpens our predictions. You\'re building something that compounds — not something that plateaus.',
    stat: '340+',
    statLabel: 'Warehouses. 2,500+ vehicles. And growing fast.',
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Data-Driven at Our Core',
    desc: 'We don\'t guess. We map real-time capacity, predict demand from harvest calendars, and route every decision through data. If you believe opinions are hypotheses that need testing, you\'ll fit right in.',
    stat: '94%',
    statLabel: 'Utilisation prediction accuracy on our platform',
  },
];

export default function WhyWorkSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100/70 border border-primary-200/50 mb-4">
            <span className="text-xs font-semibold text-primary-700 uppercase tracking-wider">Why Freshhlock</span>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary-700 mb-4">
            Work That Actually Matters
          </h2>
          <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Three reasons the best people are choosing to build their careers here.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reasons.map((r) => (
            <div key={r.title} className="group bg-card-50 rounded-xl border border-card-200 p-6 shadow-sm hover:shadow-md hover:border-primary-200/40 transition-all cursor-default flex flex-col card-animate">
              <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center mb-5 group-hover:bg-primary-200 transition-colors animate-icon-float icon-hover-pop">
                <i className={`${r.icon} text-xl text-primary-600`}></i>
              </div>
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-3">{r.title}</h3>
              <p className="text-sm text-foreground-500 leading-relaxed mb-6 flex-1">{r.desc}</p>
              <div className="pt-5 border-t border-background-200/50">
                <div className="font-heading text-2xl font-bold text-accent-500 tabular-nums mb-1">{r.stat}</div>
                <div className="text-[11px] text-foreground-400 leading-snug">{r.statLabel}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}